/**
 * FolderBatchActions · 文件夹批量/单条操作的统一数据源
 *
 * 收口下载 / 移动 / 删除的状态、handler 与两个共享弹窗（下载命名弹窗、移动 Picker）。
 * 由 AIClassifyPage 包裹 <FolderList>；FolderListToolbar 与右键菜单都通过
 * useFolderBatchActions() 消费同一套 actions，避免逻辑散落、弹窗多实例。
 */
import React, { createContext, useContext, useCallback, useState } from 'react'
import { Modal, message } from 'antd'
import {
  useAppDispatch,
  useAppSelector,
  useIsAdmin
} from '../../../../app/hooks'
import { batchDeleteFolders } from '../../../../actions/folder'
import {
  bumpFolderRefreshTick,
  exitManageMode,
  selectManageMode,
  selectManageSelectedFolderIds
} from '../../../../slices/aiClassify'
import { useFolderDownload } from '../../hooks/useFolderDownload'
import { trackClassifyClick } from '../../utils/trackClassify'
import { FolderDownloadModal } from '../DownloadModals'
import FoldersPicker from '../../../components/AddToFolder/FoldersPicker'
import type { FolderListItem } from '../../../../types/ai-classify'

interface FolderBatchActionsContextValue {
  /** 批量下载 · 操作 redux 当前选中集 */
  batchDownload: () => void
  /** 单条下载 · 右键菜单触发 */
  singleDownload: (folder: FolderListItem) => void
  /** 批量移动 · 打开 Picker */
  batchMove: () => void
  /** 单条移动 · 打开 Picker */
  singleMove: (folder: FolderListItem) => void
  /** 批量删除 · Modal.confirm 后调 batchDeleteFolders */
  batchDelete: () => void
  /** 下载导出进行中 */
  downloading: boolean
  /** 批量删除进行中 */
  deleting: boolean
}

const FolderBatchActionsContext =
  createContext<FolderBatchActionsContextValue | null>(null)

interface FolderBatchActionsProviderProps {
  groupID: string
  children: React.ReactNode
}

export const FolderBatchActionsProvider: React.FC<
  FolderBatchActionsProviderProps
> = ({ groupID, children }) => {
  const dispatch = useAppDispatch()
  const isAdmin = useIsAdmin()
  const manageMode = useAppSelector(selectManageMode)
  const selectedFolderIds = useAppSelector(selectManageSelectedFolderIds)
  const userID = useAppSelector((state: any) => state.main.user?.user_id)

  const [deleting, setDeleting] = useState(false)
  const [movingFolderIds, setMovingFolderIds] = useState<string[] | null>(null)

  const folderDownload = useFolderDownload({
    groupID,
    userID: userID as string,
    onStarted: (_folderIDs, isBatch) => {
      if (isBatch) dispatch(exitManageMode())
    }
  })

  // —— 下载 · 批量 + 单条 ——
  const batchDownload = useCallback(() => {
    if (selectedFolderIds.length === 0 || !groupID) return
    const folderIDsNumeric = selectedFolderIds
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n))
    if (folderIDsNumeric.length === 0) return
    trackClassifyClick('download')
    folderDownload.open(folderIDsNumeric, true)
  }, [selectedFolderIds, groupID, folderDownload])
  const singleDownload = useCallback(
    (folder: FolderListItem) => {
      const fid = Number(folder.folderId)
      if (!Number.isFinite(fid)) return
      trackClassifyClick('download')
      folderDownload.open([fid], false)
    },
    [folderDownload]
  )

  // —— 移动 · 批量 + 单条打开 Picker，关闭 / 成功收尾 ——
  const singleMove = useCallback((folder: FolderListItem) => {
    trackClassifyClick('moveTo', { folderName: folder.folderName })
    setMovingFolderIds([folder.folderId])
  }, [])
  const batchMove = useCallback(() => {
    if (selectedFolderIds.length === 0) return
    trackClassifyClick('moveTo')
    setMovingFolderIds([...selectedFolderIds])
  }, [selectedFolderIds])
  const handleMoveClose = useCallback(() => setMovingFolderIds(null), [])
  const handleMoveSuccess = useCallback(() => {
    setMovingFolderIds(null)
    dispatch(bumpFolderRefreshTick({}))
    if (manageMode) dispatch(exitManageMode())
  }, [dispatch, manageMode])

  // —— 批量删除 ——
  const batchDelete = useCallback(() => {
    if (selectedFolderIds.length === 0 || !groupID) return
    trackClassifyClick('delete')
    Modal.confirm({
      title: `确定删除选中的 ${selectedFolderIds.length} 个文件夹？`,
      content:
        '文件夹删除后不可恢复，文件夹内的照片不会被删除，会保留在【全部照片】中',
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onCancel: () => {
        trackClassifyClick('cancelDelete')
      },
      onOk: () => {
        trackClassifyClick('confirmDelete')
        setDeleting(true)
        // BFF 期望 folderIDs 是 number[]（与 photoFolders/Detail/HeaderExtra
        // 现有调用一致）；adaptFolder 把 folderID 在前端 String 化了，
        // 这里发请求前要转回 Number，否则后端会报 param error。
        const folderIDsNumeric = selectedFolderIds
          .map((id) => Number(id))
          .filter((n) => Number.isFinite(n))
        return dispatch(
          batchDeleteFolders({
            folderIDs: folderIDsNumeric,
            groupID
          })
        )
          .then((res: { status?: number; msg?: string } | undefined) => {
            // 走 catch（网络/4xx/5xx）→ undefined → 通用提示
            if (!res) {
              message.error('删除失败，请稍后重试')
              return
            }
            // status===0 真成功
            if (res.status === 0) {
              message.success(`已删除 ${selectedFolderIds.length} 个文件夹`)
              dispatch(bumpFolderRefreshTick({})) // 重拉列表
              dispatch(exitManageMode())
              return
            }
            // 业务拒绝：按已知 status 给具体文案，未知码兜底带状态码方便排查
            // 通用 exceptionHelper.Status 枚举：
            //   -10  notAdmin       不是管理员
            //   -56  noPermission   无操作权限
            //   -203 noFolderUploadPermission 无文件夹权限（部分接口复用此码表"无操作权限"）
            const tip = (() => {
              switch (res.status) {
                case -10:
                  return '你不是管理员，无法删除文件夹'
                case -56:
                case -203:
                  return '你没有删除权限，请联系团队管理员'
                default:
                  return `删除失败（status=${res.status}）${
                    res.msg ? ' · ' + res.msg : ''
                  }`
              }
            })()
            message.error(tip)
          })
          .finally(() => {
            setDeleting(false)
          })
      }
    })
  }, [selectedFolderIds, groupID, dispatch])

  const value: FolderBatchActionsContextValue = {
    batchDownload,
    singleDownload,
    batchMove,
    singleMove,
    batchDelete,
    downloading: folderDownload.downloading,
    deleting
  }

  return (
    <FolderBatchActionsContext.Provider value={value}>
      {children}
      {/* 下载命名弹窗 · 外层 data-no-box-select 防止弹窗内 mousedown 冒泡触发框选 */}
      <span data-no-box-select>
        <FolderDownloadModal
          visible={folderDownload.visible}
          onOk={folderDownload.confirm}
          folderPhotoTitlesList={folderDownload.titleList}
          onClose={folderDownload.close}
          isBatchDownload={folderDownload.isBatch}
          selectedFolderIds={folderDownload.folderIDs}
        />
      </span>
      {/* 移动到文件夹 · 复用 photoFolders 同款 FoldersPicker，仅 admin 可见 */}
      {movingFolderIds && isAdmin && (
        <FoldersPicker
          visible={true}
          action="move"
          showAIFolders={false}
          canSelectRoot={true}
          checkedNum={0}
          resource={{ folders: movingFolderIds, photos: [] }}
          curFolderID="0"
          place="aiClassifyFolders"
          source={5}
          onRefresh={handleMoveSuccess}
          onClose={handleMoveClose}
        />
      )}
    </FolderBatchActionsContext.Provider>
  )
}

export function useFolderBatchActions(): FolderBatchActionsContextValue {
  const ctx = useContext(FolderBatchActionsContext)
  if (!ctx) {
    throw new Error(
      'useFolderBatchActions 必须在 FolderBatchActionsProvider 内使用'
    )
  }
  return ctx
}
